import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { SourceType } from '../types/enums';
import apiService from '../services/api';

export interface NewsSource {
  id: number;
  name: string;
  url: string;
  sourceType: SourceType;
  language: string;
  reliabilityScore: number;
  biasScore?: number;
  autoProcess?: boolean;
  requiresScrapingForRelevance?: boolean;
  requiresScrapingForAnalysis?: boolean;
  scrapingConfig?: {
    selectors: string[];
    fallback?: string;
    timeout?: number;
    waitFor?: string;
  } | null;
  isActive: boolean;
  targetCountries: string[];
  relevanceThreshold?: number;
}

export interface SourcesContextValue {
  // State
  sources: NewsSource[];
  loading: boolean;
  error: string | null;
  showAddSource: boolean;
  editingSource: NewsSource | null;
  
  // Actions
  setShowAddSource: (show: boolean) => void;
  setEditingSource: (source: NewsSource | null) => void;
  fetchSources: () => void;
  createSource: (sourceData: Partial<NewsSource>) => Promise<void>;
  updateSource: (sourceId: number, sourceData: Partial<NewsSource>) => Promise<void>;
  toggleSource: (sourceId: number) => Promise<void>;
  deleteSource: (sourceId: number) => Promise<{ deleted: boolean; articlesDeleted: number; message: string }>;
  triggerScraping: (sourceId?: number) => Promise<void>;
}

const SourcesContext = createContext<SourcesContextValue | undefined>(undefined);

export function useSources(): SourcesContextValue {
  const context = useContext(SourcesContext);
  if (!context) {
    throw new Error('useSources must be used within a SourcesProvider');
  }
  return context;
}

interface SourcesProviderProps {
  children: React.ReactNode;
}

export function SourcesProvider({ children }: SourcesProviderProps) {
  const [sources, setSources] = useState<NewsSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddSource, setShowAddSource] = useState(false);
  const [editingSource, setEditingSource] = useState<NewsSource | null>(null);

  const fetchSources = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getNewsSources();
      setSources(response || []);
    } catch (err) {
      console.error('Failed to fetch sources:', err);
      setError('Failed to load news sources');
      setSources([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const createSource = useCallback(async (sourceData: Partial<NewsSource>) => {
    try {
      await apiService.createNewsSource(sourceData);
      // Refresh the list after a small delay to ensure backend has committed the changes
      setTimeout(() => fetchSources(), 100);
      setShowAddSource(false);
    } catch (err) {
      console.error('Failed to create source:', err);
      throw new Error('Failed to create news source');
    }
  }, [fetchSources]);

  const updateSource = useCallback(async (sourceId: number, sourceData: Partial<NewsSource>) => {
    try {
      await apiService.updateNewsSource(sourceId, sourceData);
      // Refresh the list after a small delay to ensure backend has committed the changes
      setTimeout(() => fetchSources(), 100);
      setEditingSource(null);
    } catch (err) {
      console.error('Failed to update source:', err);
      throw new Error('Failed to update news source');
    }
  }, [fetchSources]);

  const toggleSource = useCallback(async (sourceId: number) => {
    try {
      await apiService.toggleNewsSource(sourceId);
      // Refresh the list after a small delay to ensure backend has committed the changes
      setTimeout(() => fetchSources(), 100);
    } catch (err) {
      console.error('Failed to toggle source:', err);
      throw new Error('Failed to toggle news source');
    }
  }, [fetchSources]);

  const deleteSource = useCallback(async (sourceId: number) => {
    try {
      const result = await apiService.deleteNewsSource(sourceId);
      // Refresh the list after a small delay to ensure backend has committed the changes
      setTimeout(() => fetchSources(), 100);
      return result;
    } catch (err) {
      console.error('Failed to delete source:', err);
      throw new Error('Failed to delete news source');
    }
  }, [fetchSources]);

  const triggerScraping = useCallback(async (sourceId?: number) => {
    try {
      await apiService.triggerNewsScraping(sourceId);
    } catch (err) {
      console.error('Failed to trigger scraping:', err);
      throw new Error('Failed to trigger news scraping');
    }
  }, []);

  // Fetch sources on mount
  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  const contextValue: SourcesContextValue = {
    // State
    sources,
    loading,
    error,
    showAddSource,
    editingSource,
    
    // Actions
    setShowAddSource,
    setEditingSource,
    fetchSources,
    createSource,
    updateSource,
    toggleSource,
    deleteSource,
    triggerScraping
  };

  return (
    <SourcesContext.Provider value={contextValue}>
      {children}
    </SourcesContext.Provider>
  );
}